/*
 * Fovory strip (sections/fovory-strip.liquid).
 * - Ticker: copies the list until it covers the screen twice and moves it right to left
 *   at the section's speed (px per second). Copies are hidden from screen readers and
 *   from the keyboard; the first list stays the real one.
 * - Stops under the pointer, while something inside has focus, or with the pause button.
 *   With prefers-reduced-motion it does not move at all (the list scrolls by hand).
 * - Countdowns tick every second and the line goes away when its time is up.
 * - Language switch (desktop): a small menu of submit buttons, closes on Esc or outside click.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function format(left) {
    const d = Math.floor(left / 86400);
    const h = Math.floor((left % 86400) / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = Math.floor(left % 60);
    return (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
  }

  function init(strip) {
    if (strip.dataset.ready) return;
    strip.dataset.ready = 'true';

    const view = strip.querySelector('.fv-strip__view');
    const run = strip.querySelector('.fv-strip__run');
    const track = strip.querySelector('.fv-strip__track');
    const pp = strip.querySelector('[data-fv-strip-pp]');
    const speed = Math.max(10, parseFloat(strip.dataset.speed) || 40);
    if (!view || !run || !track) return;

    function clear() {
      run.querySelectorAll('.fv-strip__track[aria-hidden]').forEach((c) => c.remove());
    }

    function layout() {
      clear();
      const items = track.querySelectorAll('.fv-strip__i');
      if (!items.length) {
        strip.hidden = true;
        return;
      }
      if (reduce.matches) {
        strip.classList.add('is-still');
        run.style.removeProperty('--fv-strip-shift');
        run.style.removeProperty('--fv-strip-dur');
        return;
      }
      strip.classList.remove('is-still');
      const w = track.getBoundingClientRect().width;
      if (!w) return;
      const copies = Math.ceil(view.clientWidth / w) + 1;
      for (let i = 0; i < copies; i += 1) {
        const c = track.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('role');
        c.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        run.appendChild(c);
      }
      run.style.setProperty('--fv-strip-shift', -w + 'px');
      run.style.setProperty('--fv-strip-dur', (w / speed).toFixed(2) + 's');
    }

    // Countdowns.
    function tick() {
      const now = Date.now() / 1000;
      let ended = false;
      strip.querySelectorAll('[data-fv-end]').forEach((el) => {
        const left = parseInt(el.dataset.fvEnd, 10) - now;
        if (left <= 0) {
          const li = el.closest('.fv-strip__i');
          if (li && !li.hidden) {
            li.hidden = true;
            ended = true;
          }
          return;
        }
        el.textContent = format(left);
      });
      if (ended) layout();
    }
    if (strip.querySelector('[data-fv-end]')) {
      tick();
      setInterval(tick, 1000);
    }

    // Pause / play.
    if (pp) {
      pp.addEventListener('click', () => {
        const paused = strip.classList.toggle('is-paused');
        pp.setAttribute('aria-pressed', paused ? 'true' : 'false');
        pp.setAttribute('aria-label', paused ? pp.dataset.labelPlay : pp.dataset.labelPause);
      });
    }

    // Lay out once the fonts are in (widths change), and again when the width changes.
    let lastW = 0;
    const relayout = () => {
      if (view.clientWidth === lastW) return;
      lastW = view.clientWidth;
      layout();
    };
    (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(() => {
      lastW = 0;
      relayout();
    });
    if ('ResizeObserver' in window) new ResizeObserver(relayout).observe(view);
    else window.addEventListener('resize', relayout);
    reduce.addEventListener && reduce.addEventListener('change', () => { lastW = 0; relayout(); });

    // Language switch.
    const lgBtn = strip.querySelector('[data-fv-strip-lg]');
    const lgMenu = lgBtn && document.getElementById(lgBtn.getAttribute('aria-controls'));
    if (lgBtn && lgMenu) {
      const setOpen = (open) => {
        lgBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        lgMenu.hidden = !open;
      };
      lgBtn.addEventListener('click', () => {
        const open = lgBtn.getAttribute('aria-expanded') !== 'true';
        setOpen(open);
        if (open) {
          const first = lgMenu.querySelector('[aria-current="true"]') || lgMenu.querySelector('button');
          if (first) first.focus();
        }
      });
      lgMenu.addEventListener('click', (e) => {
        const b = e.target.closest('button[aria-current="true"]');
        if (b) {
          e.preventDefault();
          setOpen(false);
          lgBtn.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (lgMenu.hidden) return;
        if (!e.target.closest('.fv-strip__lg')) setOpen(false);
      });
      strip.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !lgMenu.hidden) {
          setOpen(false);
          lgBtn.focus();
        }
      });
    }
  }

  document.querySelectorAll('[data-fv-strip]').forEach(init);
  // Theme editor: re-run when the section is added or changed.
  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target.querySelector('[data-fv-strip]');
    if (s) init(s);
  });
})();
